import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Badge, useToast } from '@kollez/design-system';
import {
  usePlatformApproval,
  useApproveApproval,
  useRejectApproval,
} from '@/lib/queries';

/**
 * /platform/approvals/:id — full-page detail for one InstitutionApplication
 * or CompanyApplication, mirroring the fields the drawer surfaces but with
 * enough room for verification docs, a full action rail, and a breadcrumb
 * back to the queue.
 *
 * Kind is passed as ?kind=… when we arrive from the queue so the server can
 * hit a single collection. When missing (deep link, refresh), the server
 * probes both and infers it.
 */

interface ApprovalDetail {
  _id: string;
  kind: 'institution' | 'company';
  status: string;
  createdAt?: string;
  submittedAt?: string;
  reviewedAt?: string;
  applicantName?: string;
  applicantEmail?: string;
  applicantPhone?: string;
  applicantRole?: string;
  source?: string;
  verificationDocuments?: Array<{ kind: string; url: string; uploadedAt?: string }>;
  // institution
  institutionName?: string;
  websiteUrl?: string;
  additionalDetails?: string;
  aisheColleges?: unknown[];
  // company
  companyName?: string;
  companyWebsite?: string;
  industry?: string;
  companySize?: string;
  employeeCount?: number;
  foundedYear?: number;
  gstin?: string;
  pan?: string;
  hqCity?: string;
  hqState?: string;
  hiringTargets?: { yearlyHires?: number; campusesTargeted?: number; rolesTypical?: string[] };
  // reject audit fields (set by controller on rejection)
  rejectedReason?: string;
  rejectedBy?: string;
}

function orgName(r: ApprovalDetail): string {
  return r.kind === 'company'
    ? (r.companyName ?? '(unnamed company)')
    : (r.institutionName ?? '(unnamed institution)');
}

function humanAge(d: Date): string {
  const ms = Date.now() - d.getTime();
  const h = Math.round(ms / 3600000);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

export function PlatformApprovalDetailPage() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const nav = useNavigate();
  const toast = useToast();
  const kindHint = search.get('kind') as 'institution' | 'company' | null;
  const query = usePlatformApproval(id, kindHint ?? undefined);
  const approve = useApproveApproval();
  const reject = useRejectApproval();

  if (query.isLoading) {
    return (
      <div className="stack">
        <p className="eyebrow">Platform ops · Approval</p>
        <h1 className="h-page">Loading…</h1>
      </div>
    );
  }
  if (query.error || !query.data?.approval) {
    return (
      <div className="stack">
        <p className="eyebrow">Platform ops · Approval</p>
        <h1 className="h-page">Approval not found</h1>
        <p className="subhead">
          The application may have been removed or you don't have access.{' '}
          <Link to="/platform/approvals" className="link">Back to the queue</Link>.
        </p>
      </div>
    );
  }
  const r = query.data.approval as ApprovalDetail;
  const isTerminal = r.status === 'approved' || r.status === 'rejected';

  const doApprove = () => {
    approve.mutate(
      { id: r._id, kind: r.kind, plan: r.kind === 'institution' ? 'college' : undefined },
      {
        onSuccess: () => {
          toast.show({ message: `Provisioned ${orgName(r)}. Admin invite sent to ${r.applicantEmail ?? 'applicant'}.` });
          // Ride back to the queue so the ops user sees the row leave the pending list.
          nav('/platform/approvals');
        },
        onError: (e: unknown) => toast.show({ tone: 'warning', message: (e as Error)?.message ?? 'Approve failed' }),
      },
    );
  };
  const doReject = () => {
    const reason = window.prompt('Reason for rejection (required, kept in audit log):');
    if (!reason || reason.trim().length < 5) return;
    reject.mutate(
      { id: r._id, kind: r.kind, reason },
      {
        onSuccess: () => {
          toast.show({ message: `Rejected ${orgName(r)}. Applicant notified.` });
          nav('/platform/approvals');
        },
        onError: (e: unknown) => toast.show({ tone: 'warning', message: (e as Error)?.message ?? 'Reject failed' }),
      },
    );
  };

  return (
    <div className="stack">
      {/* Breadcrumb + header */}
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">
            <Link to="/platform/approvals" className="link">Approvals queue</Link>
            <span> · {r.kind} · {humanAge(new Date(r.createdAt ?? r.submittedAt ?? Date.now()))} old{r.source ? ` · via ${r.source}` : ''}</span>
          </p>
          <h1 className="h-page">{orgName(r)}</h1>
          <p className="subhead">
            <Badge tone={r.status === 'pending' ? 'warning' : r.status === 'in_review' ? 'info' : r.status === 'approved' ? 'success' : 'outline'}>
              {r.status.replace(/_/g, ' ')}
            </Badge>
            {r.reviewedAt && (
              <span className="text-secondary ml-2">
                · reviewed {new Date(r.reviewedAt).toLocaleString()}
              </span>
            )}
          </p>
        </div>
        <div className="ph-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={approve.isPending || isTerminal}
            onClick={doApprove}
          >
            {approve.isPending ? 'Provisioning…' : 'Approve + provision'}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={reject.isPending || isTerminal}
            onClick={doReject}
          >
            {reject.isPending ? 'Rejecting…' : 'Reject'}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled
            title="Server-side status flow TBD"
          >
            Request more info
          </button>
        </div>
      </div>

      {/* Detail grid */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Left 2/3: applicant + kind-specific block + docs */}
        <section className="card lg:col-span-2 stack">
          <h2 className="h-section">Applicant</h2>
          <dl className="dl">
            <dt>Name</dt><dd>{r.applicantName ?? '—'}</dd>
            {r.applicantRole && <><dt>Role</dt><dd>{r.applicantRole}</dd></>}
            <dt>Email</dt><dd>{r.applicantEmail ?? '—'}</dd>
            {r.applicantPhone && <><dt>Phone</dt><dd>{r.applicantPhone}</dd></>}
          </dl>

          {r.kind === 'company' ? (
            <>
              <h2 className="h-section">Company</h2>
              <dl className="dl">
                {r.industry && <><dt>Industry</dt><dd>{r.industry}</dd></>}
                {r.companySize && (
                  <>
                    <dt>Size</dt>
                    <dd>
                      {r.companySize}
                      {r.employeeCount ? ` · ${r.employeeCount.toLocaleString('en-IN')} employees` : ''}
                    </dd>
                  </>
                )}
                {r.foundedYear && <><dt>Founded</dt><dd>{r.foundedYear}</dd></>}
                {(r.hqCity || r.hqState) && (
                  <>
                    <dt>HQ</dt>
                    <dd>{[r.hqCity, r.hqState].filter(Boolean).join(', ')}</dd>
                  </>
                )}
                {r.gstin && <><dt>GSTIN</dt><dd><code>{r.gstin}</code></dd></>}
                {r.pan && <><dt>PAN</dt><dd><code>{r.pan}</code></dd></>}
                {r.companyWebsite && (
                  <>
                    <dt>Website</dt>
                    <dd>
                      <a href={r.companyWebsite} target="_blank" rel="noreferrer" className="link">
                        {r.companyWebsite}
                      </a>
                    </dd>
                  </>
                )}
              </dl>
              {r.hiringTargets && (
                <p className="text-sm">
                  <b>Hiring targets:</b>{' '}
                  {r.hiringTargets.yearlyHires ?? '?'} hires/year across{' '}
                  {r.hiringTargets.campusesTargeted ?? '?'} campuses.
                  {r.hiringTargets.rolesTypical?.length
                    ? ` Roles: ${r.hiringTargets.rolesTypical.join(', ')}.`
                    : ''}
                </p>
              )}
            </>
          ) : (
            <>
              <h2 className="h-section">Institution</h2>
              <dl className="dl">
                {r.websiteUrl && (
                  <>
                    <dt>Website</dt>
                    <dd>
                      <a href={r.websiteUrl} target="_blank" rel="noreferrer" className="link">
                        {r.websiteUrl}
                      </a>
                    </dd>
                  </>
                )}
                {Array.isArray(r.aisheColleges) && r.aisheColleges.length > 0 && (
                  <>
                    <dt>AISHE matches</dt>
                    <dd>{r.aisheColleges.length}</dd>
                  </>
                )}
              </dl>
              {r.additionalDetails && (
                <p className="text-sm text-secondary">{r.additionalDetails}</p>
              )}
            </>
          )}

          {r.verificationDocuments && r.verificationDocuments.length > 0 && (
            <>
              <h2 className="h-section">Verification documents</h2>
              <ul className="grid gap-1 text-sm">
                {r.verificationDocuments.map((d, i) => (
                  <li key={i}>
                    <span className="badge">{d.kind}</span>{' '}
                    {d.url.startsWith('stub://') ? (
                      <span className="text-secondary">{d.url} (dev stub)</span>
                    ) : (
                      <a href={d.url} target="_blank" rel="noreferrer" className="link">{d.url}</a>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          {r.status === 'rejected' && r.rejectedReason && (
            <>
              <h2 className="h-section">Rejection reason</h2>
              <p className="text-sm">{r.rejectedReason}</p>
            </>
          )}
        </section>

        {/* Right 1/3: side rail — status timeline + provenance */}
        <aside className="card stack">
          <h2 className="h-section">Timeline</h2>
          <dl className="dl">
            {r.createdAt && (
              <>
                <dt>Submitted</dt>
                <dd>{new Date(r.createdAt).toLocaleString()}</dd>
              </>
            )}
            {r.reviewedAt && (
              <>
                <dt>Reviewed</dt>
                <dd>{new Date(r.reviewedAt).toLocaleString()}</dd>
              </>
            )}
            <dt>Source</dt>
            <dd>{r.source ?? 'self-serve web'}</dd>
            <dt>Kind</dt>
            <dd>{r.kind}</dd>
          </dl>
        </aside>
      </div>
    </div>
  );
}
